import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calculator, Phone, Mail, Building2, Shield, GraduationCap, Store, Tractor, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import SimulateurMasterOSCOP from '../components/SimulateurMasterOSCOP';

export default function SimulateurMasterPage() {
  const navigate = useNavigate();
  
  const handleSimulationComplete = (_result) => {
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Button>
          
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-indigo-600" />
            <span className="font-semibold text-gray-900">Simulateur Master O'SCOP</span>
          </div>
        </div>
      </div>
      
      {/* Contenu */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Simulateur */}
          <div className="lg:col-span-2">
            <SimulateurMasterOSCOP 
              onSimulationComplete={handleSimulationComplete}
              showHeader={true}
            />
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Secteurs couverts */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Building2 className="h-5 w-5" />
                  Secteurs couverts
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-3 p-2 rounded-lg bg-blue-50">
                  <Home className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-blue-900">Habitat</p>
                    <p className="text-xs text-blue-700">Accession solidaire • Fonds FOGEDOM</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-2 rounded-lg bg-amber-50">
                  <Building2 className="h-5 w-5 text-amber-600" />
                  <div>
                    <p className="font-medium text-amber-900">BTP</p>
                    <p className="text-xs text-amber-700">Garanties professionnelles • Fonds COPPAM</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-2 rounded-lg bg-green-50">
                  <Tractor className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-green-900">Agriculture</p>
                    <p className="text-xs text-green-700">Financement solidaire • Fonds AGRO-SCOP</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-2 rounded-lg bg-purple-50">
                  <Store className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-purple-900">Commerce</p>
                    <p className="text-xs text-purple-700">Développement ESS • Fonds COMMERCE-ESS</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-2 rounded-lg bg-pink-50">
                  <GraduationCap className="h-5 w-5 text-pink-600" />
                  <div>
                    <p className="font-medium text-pink-900">Formation</p>
                    <p className="text-xs text-pink-700">Formation continue • Fonds FORMA'SCOP</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Avantages ESS */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Shield className="h-5 w-5 text-green-600" />
                  Avantages ESS
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1%</span>
                  </div>
                  <div>
                    <p className="font-medium">Adhésion O'SCOP</p>
                    <p className="text-xs text-gray-500">Réduction de 1% sur le montant total</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xs">0.5%</span>
                  </div>
                  <div>
                    <p className="font-medium">Adhésion FTPE</p>
                    <p className="text-xs text-gray-500">Réduction de 0.5% supplémentaire</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-xs">0.3%</span>
                  </div>
                  <div>
                    <p className="font-medium">Prévention active</p>
                    <p className="text-xs text-gray-500">Réduction de 0.3% avec prévention</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Contact */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Besoin d'aide ?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <a href="tel:+590590000000" className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <Phone className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium">0590 00 00 00</p>
                    <p className="text-xs text-gray-500">Du lundi au vendredi</p>
                  </div>
                </a>
                
                <a href="mailto:contact@objectifscopoutremer.com" className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium">contact@objectifscopoutremer.com</p>
                    <p className="text-xs text-gray-500">Réponse sous 48h</p>
                  </div>
                </a>
              </CardContent>
            </Card>
            
            {/* Disclaimer */}
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-xs text-amber-800">
                  <strong>Mention légale :</strong> Ce simulateur fournit une estimation indicative. 
                  Les résultats ne constituent en aucun cas une offre ni un engagement contractuel. 
                  Dispositif hors Code des assurances.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
